/**
 * Decision Dossier — the legal-grade "prove why a decision happened" view.
 *
 * Renders the full defensible record of a single agent run from
 * `GET /api/v1/governance/decision-dossier/?workspace=<id>&run=<id>`
 * (apps.governance.services.decision_dossier): what was decided, the pinned
 * decision context (model / system prompt / ontology version / retrieval
 * profile / active policies at run time), the reasoning trace, the evidence
 * (lineage), the confidence breakdown, the cost, and the audit trail.
 *
 * A one-click "Download PROV-O" export returns the same run as a PROV-O
 * JSON-LD lineage document for external auditors.
 *
 * Business framing: this answers the Compliance and CTO personas — every
 * automated decision is reproducible and explainable after the fact.
 */
import { useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Loader2, Download, ShieldCheck, AlertTriangle, Cpu, FileText,
  GitBranch, Coins, ScrollText, Gavel, Brain,
} from "lucide-react";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, EmptyState, StatusPill } from "@/components/common";
import { useApp } from "@/lib/store";
import { fmtDate } from "@/lib/utils";

interface PolicySnap { id: string; name: string; effect: string; priority: number }
interface Dossier {
  run: {
    id: string; status: string; triggered_by: string;
    started_at: string | null; finished_at: string | null;
    input: unknown; output: unknown;
  };
  context: {
    model: string; system_prompt: string; tools: unknown[];
    ontology_version: { id: string; semver: string; status: string } | null;
    retrieval_profile: { id: string; name: string } | null;
    policies: PolicySnap[];
  };
  context_pinned: boolean;
  reasoning: { seq: number; kind: string; payload: unknown }[];
  evidence: { src: string; dst: string; op: string }[];
  confidence: {
    target_kind: string; target_id: string; retrieval: number;
    ontology: number; source_trust: number; reasoning: number;
    composite: number; explanation: unknown;
  }[];
  cost: {
    provider: string; model: string; input_tokens: number;
    output_tokens: number; cost_usd: string; latency_ms: number;
  }[];
  audit: { action: string; actor_id: string; status: number | null; created_at: string }[];
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-fg">
        {icon} {title}
      </h2>
      {children}
    </div>
  );
}

function Json({ value }: { value: unknown }) {
  return (
    <pre className="overflow-auto rounded bg-bg/60 p-2 text-xs text-muted">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export function DecisionDossier() {
  const ws = useApp((s) => s.workspaceId);
  const search = useSearch({ strict: false }) as { run?: string };
  const runId = search.run;

  const q = useQuery<Dossier>({
    queryKey: ["decision-dossier", ws, runId],
    queryFn: () =>
      rawFetch<Dossier>(`/governance/decision-dossier/?workspace=${ws}&run=${runId}`),
    enabled: !!ws && !!runId,
  });

  async function downloadProv() {
    const data = await rawFetch(
      `/governance/decision-dossier/?workspace=${ws}&run=${runId}&format=prov-o`,
    );
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/ld+json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dossier-${runId}.prov.jsonld`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!ws) {
    return (
      <>
        <PageHeader title="Decision Dossier"
                    subtitle="Prove why an automated decision happened — context, reasoning, evidence and audit." />
        <EmptyState title="Select a workspace" hint="Dossiers are scoped per workspace. Pick one from the header." />
      </>
    );
  }
  if (!runId) {
    return (
      <>
        <PageHeader title="Decision Dossier"
                    subtitle="Prove why an automated decision happened — context, reasoning, evidence and audit." />
        <EmptyState title="No run selected"
                    hint="Open this page from a run in Agents → Runs → Dossier." />
      </>
    );
  }
  if (q.isLoading) {
    return (
      <div className="card flex items-center gap-2 text-sm text-muted">
        <Loader2 size={14} className="animate-spin" /> Assembling decision dossier…
      </div>
    );
  }
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data) return null;

  const d = q.data;
  const c = d.context;

  return (
    <>
      <PageHeader
        title="Decision Dossier"
        subtitle="The defensible record of why this agent run decided what it did."
        actions={
          <button className="btn btn-primary inline-flex items-center gap-1" onClick={downloadProv}>
            <Download size={14} /> Download PROV-O
          </button>
        }
      />

      {!d.context_pinned && (
        <div className="card mb-4 border-warn/40 bg-warn/5 text-sm">
          <div className="flex items-start gap-2 text-warn">
            <AlertTriangle size={16} className="mt-0.5" />
            <div>
              <p className="font-medium">Context not pinned for this run</p>
              <p className="mt-0.5 text-muted">
                This run pre-dates run-time context pinning, so the context below
                reflects the agent's <strong>current</strong> definition, not a
                point-in-time snapshot. Newer runs capture an immutable snapshot.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section icon={<FileText size={14} />} title="Decision">
          <div className="mb-2 flex items-center gap-2 text-xs text-muted">
            <StatusPill status={d.run.status} />
            <span>triggered by {d.run.triggered_by || "—"}</span>
            <span>· {fmtDate(d.run.started_at)} → {fmtDate(d.run.finished_at)}</span>
          </div>
          <div className="mb-1 text-xs font-medium text-muted">Input</div>
          <Json value={d.run.input} />
          <div className="mb-1 mt-2 text-xs font-medium text-muted">Output</div>
          <Json value={d.run.output} />
        </Section>

        <Section icon={<Cpu size={14} />} title="Context in force">
          <dl className="space-y-1 text-xs">
            <div className="flex justify-between"><dt className="text-muted">Model</dt>
              <dd className="font-mono">{c.model}</dd></div>
            <div className="flex justify-between"><dt className="text-muted">Ontology</dt>
              <dd>{c.ontology_version ? `${c.ontology_version.semver} (${c.ontology_version.status})` : "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-muted">Retrieval profile</dt>
              <dd>{c.retrieval_profile?.name ?? "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-muted">Tools</dt>
              <dd>{c.tools.length}</dd></div>
          </dl>
          <div className="mb-1 mt-2 text-xs font-medium text-muted">System prompt</div>
          <pre className="max-h-32 overflow-auto rounded bg-bg/60 p-2 text-xs text-muted whitespace-pre-wrap">
            {c.system_prompt || "—"}
          </pre>
          <div className="mb-1 mt-2 flex items-center gap-1 text-xs font-medium text-muted">
            <Gavel size={12} /> Active policies ({c.policies.length})
          </div>
          {c.policies.length === 0 ? (
            <div className="text-xs text-muted">No access policies in force.</div>
          ) : (
            <ul className="space-y-1 text-xs">
              {c.policies.map((p) => (
                <li key={p.id} className="flex items-center justify-between">
                  <span>{p.name}</span>
                  <span className={p.effect === "deny" ? "pill-bad" : "pill-good"}>
                    {p.effect} · {p.priority}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section icon={<Brain size={14} />} title={`Reasoning (${d.reasoning.length} steps)`}>
          {d.reasoning.length === 0 ? (
            <div className="text-xs text-muted">No trace recorded.</div>
          ) : (
            <ol className="space-y-1 text-xs">
              {d.reasoning.map((t) => (
                <li key={t.seq} className="flex gap-2">
                  <span className="pill">{t.kind}</span>
                  <span className="flex-1 truncate text-muted">{JSON.stringify(t.payload)}</span>
                </li>
              ))}
            </ol>
          )}
        </Section>

        <Section icon={<GitBranch size={14} />} title={`Evidence (${d.evidence.length})`}>
          {d.evidence.length === 0 ? (
            <div className="text-xs text-muted">No lineage edges recorded for this run.</div>
          ) : (
            <ul className="space-y-1 text-xs">
              {d.evidence.map((e, i) => (
                <li key={i} className="flex items-center gap-1">
                  <span className="font-mono">{e.src}</span>
                  <span className="text-muted">—{e.op}→</span>
                  <span className="font-mono">{e.dst}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section icon={<ShieldCheck size={14} />} title="Confidence">
          {d.confidence.length === 0 ? (
            <div className="text-xs text-muted">No confidence score recorded.</div>
          ) : (
            d.confidence.map((cf, i) => (
              <div key={i} className="mb-2 text-xs">
                <div className="mb-1 flex justify-between">
                  <span className="text-muted">{cf.target_kind}</span>
                  <span className="font-medium">{Math.round(cf.composite * 100)}%</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-muted">
                  <span>retrieval {Math.round(cf.retrieval * 100)}%</span>
                  <span>ontology {Math.round(cf.ontology * 100)}%</span>
                  <span>source {Math.round(cf.source_trust * 100)}%</span>
                  <span>reasoning {Math.round(cf.reasoning * 100)}%</span>
                </div>
              </div>
            ))
          )}
        </Section>

        <Section icon={<Coins size={14} />} title="Cost">
          {d.cost.length === 0 ? (
            <div className="text-xs text-muted">No cost recorded for this run.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-muted">
                <tr><th className="text-left">Provider</th><th className="text-left">Model</th>
                  <th className="text-right">Tokens</th><th className="text-right">USD</th></tr>
              </thead>
              <tbody>
                {d.cost.map((co, i) => (
                  <tr key={i}>
                    <td>{co.provider}</td><td className="font-mono">{co.model}</td>
                    <td className="text-right">{co.input_tokens + co.output_tokens}</td>
                    <td className="text-right">${co.cost_usd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>

      <div className="mt-4">
        <Section icon={<ScrollText size={14} />} title={`Audit trail (${d.audit.length})`}>
          {d.audit.length === 0 ? (
            <div className="text-xs text-muted">No audit events recorded for this run.</div>
          ) : (
            <ul className="space-y-1 text-xs">
              {d.audit.map((a, i) => (
                <li key={i} className="flex items-center justify-between">
                  <span><span className="font-medium">{a.action}</span> · {a.actor_id}</span>
                  <span className="text-muted">{fmtDate(a.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </>
  );
}
